import { ADALONG_COLORS } from '@src/styles';
import { classes, stylesheet } from 'typestyle';
import type { AnalyticsTimeframe, AnalyticsUnit } from 'common/dist/types/apiTypes';
import { AnalyticsUnitTimeframePicker } from './AnalyticsUnitTimeframePicker';

export type AnalyticsView = 'ecommerce' | 'ugc';

interface Props {
  activeView: AnalyticsView;
  unit: AnalyticsUnit;
  timeframe: AnalyticsTimeframe;
  numericTimeframe?: number;
  onViewChange: (view: AnalyticsView) => void;
  onUnitChange: (unit: AnalyticsUnit) => void;
  onTimeframeChange: (timeframe: AnalyticsTimeframe) => void;
}

const VIEWS: { value: AnalyticsView; label: string }[] = [
  { value: 'ecommerce', label: 'E-COMMERCE' },
  { value: 'ugc', label: 'UGC' },
];

export function AnalyticsHeader({
  activeView,
  unit,
  timeframe,
  numericTimeframe,
  onViewChange,
  onUnitChange,
  onTimeframeChange,
}: Props) {
  return (
    <header className={sheet.header}>
      <div className={sheet.tabs} role="tablist" aria-label="Analytics dashboard">
        {VIEWS.map(({ value, label }) => {
          const isActive = activeView === value;
          return (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`analytics-${value}-panel`}
              className={classes(sheet.tab, isActive && sheet.tabActive)}
              onClick={() => onViewChange(value)}
            >
              {label}
            </button>
          );
        })}
      </div>

      <AnalyticsUnitTimeframePicker
        unit={unit}
        timeframe={timeframe}
        numericTimeframe={numericTimeframe}
        onUnitChange={onUnitChange}
        onTimeframeChange={onTimeframeChange}
      />
    </header>
  );
}

const sheet = stylesheet({
  header: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 24,
    padding: '8px 32px 20px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    flexShrink: 0,
    $nest: {
      '@media (max-width: 980px)': {
        alignItems: 'stretch',
        flexDirection: 'column',
        padding: '8px 20px 16px',
      },
    },
  },
  tabs: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 10,
  },
  tab: {
    appearance: 'none',
    border: 0,
    borderBottom: '3px solid transparent',
    background: 'transparent',
    color: ADALONG_COLORS.LIGHT_GRAY,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 15,
    fontWeight: 600,
    letterSpacing: 0.3,
    padding: '12px 14px 10px',
    transition: 'border-color 150ms ease, color 150ms ease',
    $nest: {
      '&:hover': {
        color: ADALONG_COLORS.SMOOTH_WHITE,
      },
      '&:focus-visible': {
        outline: `2px solid ${ADALONG_COLORS.FLASH_BLUE}`,
        outlineOffset: 2,
      },
    },
  },
  tabActive: {
    borderBottomColor: ADALONG_COLORS.FLASH_BLUE,
    color: ADALONG_COLORS.LIGHT_WHITE,
  },
});
